import React, { useEffect, useState } from 'react';
import BaseWidget from '../../../widgets/BaseWidget';
import DynamicDropdown from '../../../widgets/dynamic/DynamicDropdown';
import DynamicSlider from '../../../widgets/dynamic/DynamicSlider';
import DynamicMultiselect from '../../../widgets/dynamic/DynamicMultiselect';
import DynamicIntField from '../../../widgets/dynamic/DynamicIntField';
import DynamicStringField from '../../../widgets/dynamic/DynamicStringField';
import ImageSizeInput from '../../../ui/inputs/ImageSizeInput';
import SeedInput from '../../../ui/inputs/SeedInput';
import { Icon, ICONS } from '../../../ui/icons';
import { useLocalization, type Language } from '../../../../config/localization';
import { getControllerField, getEffectiveConfig, resolveFieldConfig } from '../../../../utils/controllerVariants';
import { nextRandomSeed } from '../../../../utils/seedRolling';

interface Field {
  id: number;
  field_type: 'dropdown' | 'slider' | 'multiselect' | 'int_field' | 'string_field' | 'image_size' | 'seed' | 'dropdown_prompt' | 'controller_dropdown' | 'hidden_text' | 'hidden_number' | 'hidden_prompt';
  field_id: string;
  label: string;
  tooltip?: string;
  display_order: number;
  config_data: string;
}

interface DynamicWidgetPreviewProps {
  widgetName: string;
  widgetDescription?: string;
  fields: Field[];
  selectedLanguage: Language;
}

export default function DynamicWidgetPreview({
  widgetName,
  widgetDescription,
  fields,
  selectedLanguage,
}: DynamicWidgetPreviewProps) {
  const t = useLocalization(selectedLanguage);
  const [values, setValues] = useState<Record<string, any>>({});
  const [fixedSeeds, setFixedSeeds] = useState<Record<string, boolean>>({});

  // Build FieldLike objects for variant resolution
  const fieldLikes = fields.map(f => {
    let parsedConfig: Record<string, any> = {};
    try { parsedConfig = JSON.parse(f.config_data); } catch { /* empty */ }
    return { field_type: f.field_type, config_data: parsedConfig, field_id: f.field_id };
  });
  const controllerField = getControllerField(fieldLikes);
  const controllerValue = controllerField ? (values[controllerField.field_id] ? String(values[controllerField.field_id]) : undefined) : undefined;

  // SeedInput is controlled and deliberately does not invent state. Mirror the
  // runtime renderers by giving preview seed fields a real initial value.
  useEffect(() => {
    setValues(previous => {
      const next = { ...previous };
      let changed = false;

      fields.forEach(field => {
        if (field.field_type !== 'seed' || next[field.field_id] != null) return;
        let config: Record<string, any> = {};
        try {
          config = JSON.parse(field.config_data);
          if (controllerField) {
            const { variants, ...baseConfig } = config;
            config = resolveFieldConfig(baseConfig, variants, controllerValue).config;
          }
        } catch {
          // Invalid draft JSON uses the same safe default bounds as runtime.
        }
        next[field.field_id] = config.default_value
          ?? nextRandomSeed(config.min_value ?? 0, config.max_value ?? 2147483647);
        changed = true;
      });

      return changed ? next : previous;
    });
  }, [fields, controllerField?.field_id, controllerValue]);

  const handleFieldChange = (fieldId: string, value: any) => {
    setValues(prev => ({ ...prev, [fieldId]: value }));
  };

  const renderField = (field: Field) => {
    const value = values[field.field_id];
    let config;
    
    try {
      config = JSON.parse(field.config_data);

      // Apply variant resolution for non-controller fields
      if (field.field_type !== 'controller_dropdown' && controllerField) {
        const { variants, ...baseConfig } = config;
        const resolved = resolveFieldConfig(baseConfig, variants, controllerValue);
        config = resolved.config;
      }

      // Convert field names for compatibility with dynamic components
      if (field.field_type === 'slider') {
        // DynamicSlider expects min/max, but data is stored as min_value/max_value
        if ('min_value' in config) {
          config.min = config.min_value;
          delete config.min_value;
        }
        if ('max_value' in config) {
          config.max = config.max_value;
          delete config.max_value;
        }
      }

      // Convert string_field config from snake_case to camelCase
      if (field.field_type === 'string_field') {
        if ('min_length' in config) {
          config.minLength = config.min_length;
          delete config.min_length;
        }
        if ('max_length' in config) {
          config.maxLength = config.max_length;
          delete config.max_length;
        }
      }
      
      // Convert options string to array for dropdown/multiselect/controller_dropdown
      if (field.field_type === 'dropdown' || field.field_type === 'multiselect' || field.field_type === 'controller_dropdown') {
        if (!config.options) {
          config.options = [];
        } else if (typeof config.options === 'string') {
          config.options = config.options.split('\n').filter(Boolean).map((opt: string) => ({
            value: opt.trim(),
            label: opt.trim()
          }));
        } else if (Array.isArray(config.options)) {
          // Ensure all items are objects with value and label
          config.options = config.options.map((opt: any) => {
            if (typeof opt === 'string') {
              return { value: opt, label: opt };
            } else if (opt && typeof opt === 'object' && 'value' in opt) {
              return { value: opt.value, label: opt.label || opt.value };
            }
            return { value: '', label: '' };
          }).filter((opt: any) => opt.value); // Remove empty options
        }
        
        // Ensure options is always an array
        if (!Array.isArray(config.options)) {
          config.options = [];
        }
      }
    } catch (e) {
      console.error('Error parsing field config:', e);
      config = {};
    }

    switch (field.field_type) {
      case 'dropdown':
      case 'controller_dropdown':
        return (
          <DynamicDropdown
            key={field.id}
            fieldId={field.field_id}
            label={field.label}
            tooltip={field.tooltip}
            value={value || ''}
            onChange={(val) => handleFieldChange(field.field_id, val)}
            config={{ ...config, options: config.options || [] }}
            language="en"
          />
        );

      case 'dropdown_prompt': {
        const dpOptions = Array.isArray(config.options)
          ? config.options
              .filter((opt: any) => opt.label && opt.prompt_id)
              .map((opt: any) => ({ label: opt.label, value: String(opt.prompt_id) }))
          : [];
        return (
          <DynamicDropdown
            key={field.id}
            fieldId={field.field_id}
            label={field.label}
            tooltip={field.tooltip}
            value={value || (config.default_value ? String(config.default_value) : '')}
            onChange={(val) => handleFieldChange(field.field_id, val)}
            config={{ ...config, options: dpOptions }}
            language="en"
          />
        );
      }

      case 'slider':
        return (
          <DynamicSlider
            key={field.id}
            fieldId={field.field_id}
            label={field.label}
            tooltip={field.tooltip}
            value={value || config.default_value || config.min || 0}
            onChange={(val) => handleFieldChange(field.field_id, val)}
            config={config}
          />
        );
        
      case 'multiselect':
        return (
          <DynamicMultiselect
            key={field.id}
            fieldId={field.field_id}
            label={field.label}
            tooltip={field.tooltip}
            value={value || []}
            onChange={(val) => handleFieldChange(field.field_id, val)}
            config={{ ...config, options: config.options || [] }}
            language="en"
          />
        );
        
      case 'int_field':
        return (
          <DynamicIntField
            key={field.id}
            fieldId={field.field_id}
            label={field.label}
            tooltip={field.tooltip}
            value={value || config.default_value || 0}
            onChange={(val) => handleFieldChange(field.field_id, val)}
            config={config}
          />
        );
        
      case 'string_field':
        return (
          <DynamicStringField
            key={field.id}
            fieldId={field.field_id}
            label={field.label}
            tooltip={field.tooltip}
            value={value || ''}
            onChange={(val) => handleFieldChange(field.field_id, val)}
            config={config}
          />
        );
        
      case 'image_size':
        const imageSizeValue = value || { width: config.default_width || 768, height: config.default_height || 1024 };
        return (
          <div key={field.id} className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-gray-300">
                {field.label}
              </label>
              {field.tooltip && (
                <div className="w-4 h-4 bg-gray-700/50 border border-gray-600 rounded-full flex items-center justify-center hover:bg-gray-600/50 transition-colors duration-200 cursor-help" title={field.tooltip}>
                  <span className="text-xs font-medium text-gray-300">?</span>
                </div>
              )}
            </div>
            <ImageSizeInput
              value={imageSizeValue}
              onChange={(val) => handleFieldChange(field.field_id, val)}
              config={{
                min_width: config.min_width || 64,
                max_width: config.max_width || 2048,
                min_height: config.min_height || 64,
                max_height: config.max_height || 2048,
                default_width: config.default_width || 768,
                default_height: config.default_height || 1024,
                step: config.step || 8,
                aspect_ratios: config.aspect_ratios,
                default_aspect_ratio: config.default_aspect_ratio
              }}
              className="w-full"
            />
          </div>
        );
        
      case 'seed':
        const seedValue = value ?? config.default_value ?? null;
        return (
          <div key={field.id} className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-gray-300">
                {field.label}
              </label>
              {field.tooltip && (
                <div className="w-4 h-4 bg-gray-700/50 border border-gray-600 rounded-full flex items-center justify-center hover:bg-gray-600/50 transition-colors duration-200 cursor-help" title={field.tooltip}>
                  <span className="text-xs font-medium text-gray-300">?</span>
                </div>
              )}
            </div>
            <SeedInput
              value={seedValue}
              onChange={(val) => handleFieldChange(field.field_id, val)}
              checked={fixedSeeds[field.field_id] ?? false}
              onFixedChange={(checked) => setFixedSeeds(prev => ({ ...prev, [field.field_id]: checked }))}
              fixedLabel={t.fixedSeedLabel}
              fixedTooltip={t.fixedSeedTooltip}
              config={{
                min_value: config.min_value ?? 0,
                max_value: config.max_value ?? 2147483647
              }}
              className="w-full"
            />
          </div>
        );
        
      case 'hidden_text':
      case 'hidden_number':
      case 'hidden_prompt':
        return null;

      default:
        return null;
    }
  };

  return (
    <BaseWidget
      title={widgetName}
      icon={<Icon name={ICONS.SETTINGS} size={16} className="text-current" /> as any}
      description={widgetDescription}
      language={selectedLanguage}
      className=""
    >
      <div className="space-y-4">
        {fields.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">
            No fields configured yet
          </p>
        ) : (
          fields
            .sort((a, b) => a.display_order - b.display_order)
            .reduce<React.ReactElement[]>((acc, field) => {
              // Hidden field types are never rendered in preview
              if (field.field_type === 'hidden_text' || field.field_type === 'hidden_number' || field.field_type === 'hidden_prompt') return acc;

              // Apply variant resolution — hide fields when variant says hidden
              if (field.field_type !== 'controller_dropdown' && controllerField) {
                const fl = fieldLikes.find(fl => fl.field_id === field.field_id);
                if (fl) {
                  const { hidden } = getEffectiveConfig(fl, controllerValue);
                  if (hidden) return acc;
                }
              }

              let config;
              try {
                config = JSON.parse(field.config_data);
                // Convert field names for slider
                if (field.field_type === 'slider') {
                  if ('min_value' in config) {
                    config.min = config.min_value;
                  }
                  if ('max_value' in config) {
                    config.max = config.max_value;
                  }
                }
              } catch (e) {
                config = {};
              }

              const isSpecialSlider = field.field_type === 'slider' && config.design === 'special1';
              const visibleIndex = acc.length;

              acc.push(
                <React.Fragment key={field.id}>
                  {visibleIndex > 0 && <div className="border-t border-gray-700/50"></div>}

                  <div className={isSpecialSlider ? "bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-blue-500/40 rounded-lg p-4" : ""}>
                    {renderField(field)}
                  </div>
                </React.Fragment>
              );
              return acc;
            }, [])
        )}
      </div>
    </BaseWidget>
  );
}
